import { useState, useRef, useCallback, useEffect } from 'react';
import useSounds from './useSounds';

const SILENCE_THRESHOLD = 0.01;
const MAX_RECORDING_TIME = 60; // 1 minute for kids

const analyzeAudioForSilence = async (blob: Blob): Promise<boolean> => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const channelData = audioBuffer.getChannelData(0);

    let sumSquares = 0;
    for (let i = 0; i < channelData.length; i++) {
      sumSquares += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(sumSquares / channelData.length);

    await audioContext.close();
    return rms > SILENCE_THRESHOLD;
  } catch (err) {
    console.error('Error analyzing audio:', err);
    return true;
  }
};

interface UseAudioRecorderReturn {
  isRecording: boolean;
  audioBlob: Blob | null;
  error: string | null;
  recordingTime: number;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  resetRecording: () => void;
  isPlaying: boolean;
  playRecording: () => void;
  stopPlayback: () => void;
}

const useAudioRecorder = (): UseAudioRecorderReturn => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const { playSound } = useSounds();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number>(0);

  useEffect(() => {
    if (isRecording) {
      recordingStartTimeRef.current = Date.now();
      setRecordingTime(0);

      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        setRecordingTime(elapsed);

        if (elapsed >= MAX_RECORDING_TIME) {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            playSound('stopRecording');
          }
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording, playSound]);

  const startRecording = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      setAudioBlob(null);
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());

        const hasSound = await analyzeAudioForSilence(blob);
        if (!hasSound) {
          setError("I can't hear you! Please speak louder and try again.");
          return;
        }

        setAudioBlob(blob);
      };

      mediaRecorder.onerror = () => {
        setError('Oops! Something went wrong. Try again!');
      };

      mediaRecorder.start();
      setIsRecording(true);
      playSound('startRecording');
    } catch (err) {
      const error = err as Error & { name: string };
      if (error.name === 'NotAllowedError') {
        setError('Please allow microphone access to record!');
      } else if (error.name === 'NotFoundError') {
        setError('No microphone found! Please connect one and try again.');
      } else {
        setError('Could not start recording. Please try again!');
      }
      console.error('Error starting recording:', err);
    }
  }, [playSound]);

  const stopRecording = useCallback((): void => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      playSound('stopRecording');
    }
  }, [isRecording, playSound]);

  const resetRecording = useCallback((): void => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    setIsPlaying(false);
    setAudioBlob(null);
    setError(null);
  }, []);

  const playRecording = useCallback((): void => {
    if (!audioBlob) return;

    if (audioElementRef.current) {
      audioElementRef.current.pause();
    }

    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    audioElementRef.current = audio;

    audio.onplay = () => setIsPlaying(true);
    audio.onended = () => {
      setIsPlaying(false);
      URL.revokeObjectURL(audioUrl);
      audioElementRef.current = null;
    };
    audio.onerror = () => {
      setIsPlaying(false);
      setError('Could not play recording');
      URL.revokeObjectURL(audioUrl);
      audioElementRef.current = null;
    };

    audio.play().catch(() => {
      setIsPlaying(false);
      setError('Could not play recording');
      URL.revokeObjectURL(audioUrl);
      audioElementRef.current = null;
    });
  }, [audioBlob]);

  const stopPlayback = useCallback((): void => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  return {
    isRecording,
    audioBlob,
    error,
    recordingTime,
    startRecording,
    stopRecording,
    resetRecording,
    isPlaying,
    playRecording,
    stopPlayback
  };
};

export default useAudioRecorder;
